import { EntityTypes } from 'constants/entities';
import { RootState } from 'types';
import { ActionType, IActionDAO, IActionNode } from 'types/domain/action';
import { IEntityReducer } from 'core/reducers/entities';
import { getEntities } from 'core/selectors/utils/getEntities';
import { CourseNodeType, ICourse, ICourseNodeDAO } from 'types/domain/course';
import { ICourseOut } from 'types/domain/course';
import { Edge, Node } from '@xyflow/react';
import { maxBy, minBy } from 'lodash';
import { selectOfflineId } from './offlineId';
import { reduceArrayById } from 'utils/reduceArrayById';
import { IKeyArray } from 'types/IKeyArray';
import { Handles } from 'constants/handles';
import { parseActionDAO } from '../product/action/action';
import { isReviewNode, parseCourseNodeDAO } from './courseNode';
import { IModuleOut, ModuleType } from 'types/domain/module';
import { parseActionPlaceholders } from '../product/action/actionPlaceholders';
import { createCachedSelector } from 're-reselect';
import { INode } from 'types/domain/base';
import { countRecomputes, noteCacheKey } from 'core/middleware/perf';

export interface IEdge {
  id: string;
  source: string;
  sourceHandle: string;
  target: string;
  targetHandle: string;
  data: { id: number; type: EntityTypes; delay: number };
  type: 'buttonedge';
}

export interface INodeProps {
  id: number;
  children: Node<INodeProps>[];
  origin: INode | { x: number; y: number };
  childId: number;
  type: ActionType | CourseNodeType;
  delay: number;
  name?: string;
  parentActionId?: number;
  [key: string]: unknown;
  xPos?: number;
  yPos?: number;
}

/**
 * Deliberately ignores any second argument. reselect forwards every argument it
 * receives down to its input selectors, so a caller doing
 * `someSelector(state, sceneId)` anywhere in the chain above this one used to
 * land that scene/action id here and use it as the module id — producing a
 * separate cache entry per scene. Measured in the module editor: nine live
 * cache keys (one module, seven scenes, one action), which the cached selector
 * then alternated between, changing result identity hundreds of times per
 * interaction without ever recomputing. The selected module is the only module
 * this selector has ever meant, so read it from state and nowhere else.
 */
const moduleId = (state: RootState) => state.state[EntityTypes.MODULE];
const courseId = (state: RootState, id?) => id || state.state[EntityTypes.COURSE];
const selectAll = (state: RootState, id?, all?) => all;
const entity = (state: RootState) => state.entity.present;

const offlineId = selectOfflineId;

const height = 100;

const widthWithNode = 450;
const modAutoPositions = {};

/**
 * Shared "no module" result. Returning a fresh `{ nodes: [], edges: [] }` here
 * meant every call during a module-id gap handed back a new reference, which
 * cascaded into a full action-feed rebuild downstream.
 */
const EMPTY_GRAPH: { nodes: Node<INodeProps>[]; edges: Edge[] } = Object.freeze({
  nodes: Object.freeze([]) as unknown as Node<INodeProps>[],
  edges: Object.freeze([]) as unknown as Edge[],
});

/**
 * Scenario layout. Branching modules fan out far wider than the flat
 * `x + 250 / y + 100` offsets the linear layout uses, so siblings ended up
 * stacked on top of each other (a node is 370 wide and 280+ tall once its
 * options render).
 *
 * One column per depth. Children sit in a tight stack centred on their parent,
 * then each column is swept for overlaps and only the nodes that actually
 * collide get pushed apart. Reserving a band per subtree instead (the textbook
 * tidy tree) throws the first split metres apart to make room for everything
 * downstream; sweeping keeps the opening close together and lets the flow fan
 * out as the branching does.
 */
const SCENARIO_NODE_WIDTH = 370;
const SCENARIO_COLUMN_GAP = 140;
const SCENARIO_ROW_GAP = 60;
const SCENARIO_HEADER_HEIGHT = 92;
const SCENARIO_BODY_HEIGHT = 190;
const SCENARIO_OPTION_HEIGHT = 48;
const SCENARIO_PLACEHOLDER_HEIGHT = 120;

function scenarioNodeHeight(node: Node<INodeProps>, actionsById: IKeyArray<IActionNode>) {
  if (node.data.type === ActionType.FUTURE) return SCENARIO_PLACEHOLDER_HEIGHT;

  const action = actionsById[node.data.id as number];
  const options = action ? Object.values(action.entities || []).filter(e => e && e.status > -1).length : 0;

  return SCENARIO_HEADER_HEIGHT + SCENARIO_BODY_HEIGHT + options * SCENARIO_OPTION_HEIGHT;
}

/**
 * Children stacked in handle order, which is also the order they read down the
 * side of the node: fail on top, then the default out, then the options by
 * entity id. Placeholders sit on their own handle, so they slot into the same
 * order rather than being tacked on the end.
 */
function scenarioChildIds(
  node: Node<INodeProps>,
  actionsById: IKeyArray<IActionNode>,
  placeholdersByParent: IKeyArray<Node<INodeProps>[]>,
) {
  const action = actionsById[node.data.id as number];
  const children: { handle: number; id: string }[] = [];

  if (action) {
    if (!action.outs?.[Handles.DEFAULT] && action.childId)
      children.push({ handle: Handles.DEFAULT, id: `${action.childId}` });

    Object.values(action.outs || {})
      .filter((out: IModuleOut) => out.targetId)
      .forEach((out: IModuleOut) => children.push({ handle: out.sourceHandle, id: `${out.targetId}` }));
  }

  (placeholdersByParent[node.data.id as number] || []).forEach(ph =>
    children.push({ handle: (ph.data.sourceHandle as number) ?? Handles.DEFAULT, id: ph.id }),
  );

  return children.sort((a, b) => a.handle - b.handle).map(child => child.id);
}

/**
 * One tidy pass. `released` names the dragged nodes whose saved position has
 * been given up on (see `layoutScenarioNodes`); everything else with a `nodeId`
 * stays where the user put it.
 */
function layoutScenarioPass(nodes: Node<INodeProps>[], actionsById: IKeyArray<IActionNode>, released: Set<string>) {
  const nodesById: IKeyArray<Node<INodeProps>> = nodes.reduce((prev, n) => ({ ...prev, [n.id]: n }), {});

  const placeholdersByParent: IKeyArray<Node<INodeProps>[]> = nodes
    .filter(n => n.data.type === ActionType.FUTURE && n.data.parentActionId)
    .reduce((prev, n) => {
      const parentId = n.data.parentActionId as number;
      return { ...prev, [parentId]: [...(prev[parentId] || []), n] };
    }, {});

  // Claim each node for a single parent so loops back into the flow don't
  // recurse forever and don't drag an already-placed subtree around with them.
  const claimed = new Set<string>();
  const childrenById: IKeyArray<Node<INodeProps>[]> = {};

  function claimChildren(node: Node<INodeProps>) {
    const children = scenarioChildIds(node, actionsById, placeholdersByParent)
      .map(id => nodesById[id])
      .filter(child => child && child.id !== node.id && !claimed.has(child.id));

    children.forEach(child => claimed.add(child.id));
    childrenById[node.id] = children;
    children.forEach(claimChildren);
  }

  // `nodes` is already in traversal order, so the first unclaimed node is the
  // start of the flow and anything left over is a detached branch.
  const roots: Node<INodeProps>[] = [];
  nodes.forEach(node => {
    if (claimed.has(node.id)) return;
    claimed.add(node.id);
    roots.push(node);
    claimChildren(node);
  });

  const heights: IKeyArray<number> = {};
  nodes.forEach(node => (heights[node.id] = scenarioNodeHeight(node, actionsById)));

  const anchored = (node: Node<INodeProps>) => !!node.data.nodeId && !released.has(node.id);

  const move = (node: Node<INodeProps>, x: number, y: number) => {
    // A node the user has dragged keeps its saved position; its children are
    // laid out from wherever they put it.
    if (anchored(node)) return;
    node.position = { x, y };
    node.data.origin = node.position;
  };

  // Column 0 is the roots, stacked just far enough apart to clear each other.
  const rootLeft = roots[0].position?.x || 0;
  let rootTop = roots[0].position?.y || 0;

  roots.forEach(root => {
    move(root, rootLeft, rootTop);
    rootTop = root.position.y + heights[root.id] + SCENARIO_ROW_GAP;
  });

  let column = roots;

  while (column.length) {
    // Hand each node's real children a tight stack centred on it, then let the
    // sweep below open up only the places where two stacks would overlap.
    // Placeholders are laid out afterwards, around that stack: counting them in
    // it pushed the real branch off its parent's line by half a placeholder at
    // every question, which is what walked a generated scenario down the canvas
    // instead of leaving it running straight across.
    const next: Node<INodeProps>[] = [];
    const stubs: {
      x: number;
      first: Node<INodeProps>;
      last: Node<INodeProps>;
      above: Node<INodeProps>[];
      below: Node<INodeProps>[];
    }[] = [];

    column.forEach(node => {
      const children = childrenById[node.id] || [];
      if (!children.length) return;

      const isStub = (child: Node<INodeProps>) => child.data.type === ActionType.FUTURE;
      const real = children.filter(child => !isStub(child));
      // Nothing real to hang off: the placeholders are the branch, so they take
      // the parent's line themselves.
      const stack = real.length ? real : children;
      const x = node.position.x + SCENARIO_NODE_WIDTH + SCENARIO_COLUMN_GAP;

      const stackHeight = stack.reduce((total, child, i) => total + heights[child.id] + (i ? SCENARIO_ROW_GAP : 0), 0);

      let childTop = node.position.y + heights[node.id] / 2 - stackHeight / 2;

      stack.forEach(child => {
        move(child, x, childTop);
        childTop += heights[child.id] + SCENARIO_ROW_GAP;
        next.push(child);
      });

      // A placeholder reading above the first real branch stays above it, the
      // rest go below — the handle order the children arrived in, minus any say
      // in where the real branches sit.
      // No real branch means the placeholders already took the stack above.
      if (real.length && real.length < children.length) {
        const first = children.indexOf(real[0]);
        stubs.push({
          x,
          first: real[0],
          last: real[real.length - 1],
          above: children.filter((child, i) => isStub(child) && i < first),
          below: children.filter((child, i) => isStub(child) && i > first),
        });
      }
    });

    // Sweep top to bottom, pushing overlapping nodes down, then shift the whole
    // column back up so it stays centred where it wanted to be rather than
    // drifting downwards with every collision.
    const sorted = [...next].sort((a, b) => a.position.y - b.position.y);
    const centre = (column: Node<INodeProps>[]) =>
      column.length
        ? (column[0].position.y + column[column.length - 1].position.y + heights[column[column.length - 1].id]) / 2
        : 0;

    const wanted = centre(sorted);
    let limit = -Infinity;

    sorted.forEach(node => {
      if (!anchored(node) && node.position.y < limit) move(node, node.position.x, limit);
      limit = node.position.y + heights[node.id] + SCENARIO_ROW_GAP;
    });

    const shift = wanted - centre(sorted);
    if (shift) sorted.forEach(node => move(node, node.position.x, node.position.y + shift));

    // Placeholders hang off the settled stack, then get nudged down out of
    // anything they landed on. They give way to the real flow, never the other
    // way round.
    stubs.forEach(({ x, first, last, above, below }) => {
      let top = first.position.y;

      [...above].reverse().forEach(stub => {
        top -= heights[stub.id] + SCENARIO_ROW_GAP;
        move(stub, x, top);
        next.push(stub);
      });

      let bottom = last.position.y + heights[last.id] + SCENARIO_ROW_GAP;

      below.forEach(stub => {
        move(stub, x, bottom);
        bottom += heights[stub.id] + SCENARIO_ROW_GAP;
        next.push(stub);
      });
    });

    next
      .filter(node => node.data.type === ActionType.FUTURE && !anchored(node))
      .forEach(stub => {
        const clash = () =>
          next.find(
            other =>
              other !== stub &&
              other.position.x === stub.position.x &&
              other.position.y < stub.position.y + heights[stub.id] + SCENARIO_ROW_GAP &&
              stub.position.y < other.position.y + heights[other.id] + SCENARIO_ROW_GAP,
          );

        for (let other = clash(); other; other = clash())
          move(stub, stub.position.x, other.position.y + heights[other.id] + SCENARIO_ROW_GAP);
      });

    column = next;
  }

  return heights;
}

/**
 * A dragged node keeps its saved position — but only while that position still
 * works. Anything saved against a different node size (or dragged onto a branch
 * that has since grown) leaves the node sitting on top of its neighbours, and
 * because it is anchored nothing in the tidy pass can move it or route around
 * it. So: lay out, look for a dragged node overlapping anything, give up on its
 * saved position, lay out again. Each pass releases at least one node, so this
 * settles at the fully tidy layout in the worst case.
 */
export function layoutScenarioNodes(nodes: Node<INodeProps>[], actionsById: IKeyArray<IActionNode>) {
  if (!nodes.length) return;

  const released = new Set<string>();

  for (;;) {
    const heights = layoutScenarioPass(nodes, actionsById, released);

    // Placeholders sit out of the test: they are a fraction of a node wide and
    // already give way to everything around them, so a "+" near a dragged node
    // is not a reason to move the node.
    const placed = nodes.filter(node => node.data.type !== ActionType.FUTURE);

    const overlaps = (a: Node<INodeProps>, b: Node<INodeProps>) =>
      a.position.x < b.position.x + SCENARIO_NODE_WIDTH &&
      b.position.x < a.position.x + SCENARIO_NODE_WIDTH &&
      a.position.y < b.position.y + heights[b.id] &&
      b.position.y < a.position.y + heights[a.id];

    const clashing = placed.filter(
      node =>
        !!node.data.nodeId && !released.has(node.id) && placed.some(other => other !== node && overlaps(node, other)),
    );

    if (!clashing.length) return;

    clashing.forEach(node => released.add(node.id));
  }
}

export const selectModuleNodesAndEdges = createCachedSelector(
  entity,
  moduleId,
  offlineId,
  countRecomputes(
    'selectModuleNodesAndEdges',
    (entity: IEntityReducer, id: number, offlineId): { nodes: Node<INodeProps>[]; edges: Edge[] } => {
      if (!id) return EMPTY_GRAPH;
      const width = 250;

      const actionsById: IKeyArray<IActionNode> = reduceArrayById(
        getEntities(entity, EntityTypes.ACTION, 'moduleId', id).map((action: IActionDAO, index) =>
          parseActionDAO(entity, action.id),
        ),
      );

      const nodes: Node<INodeProps>[] = [];
      const edges: Edge[] = [];
      let index: number = offlineId;

      function convertNode(id, index) {
        if (!actionsById[id]) return null;
        if (actionsById[id].childId && !edges.find(e => e.id === id.toString()))
          edges.push(convertEdge(id, id, actionsById[id].childId));

        return {
          id: `${id}`,
          data: {
            id,
            childId: actionsById[id].childId,
            index,
            nodeId: actionsById[id].nodeId,
            origin:
              actionsById[id].nodeId && entity[EntityTypes.NODE][actionsById[id].nodeId]
                ? entity[EntityTypes.NODE][actionsById[id].nodeId]
                : actionsById[id].node || { x: 0, y: 0 },
            children: [],
            type: actionsById[id].type,
            delay: 0,
          },
          type: EntityTypes.ACTION,
          position:
            actionsById[id].nodeId && entity[EntityTypes.NODE][actionsById[id].nodeId]
              ? entity[EntityTypes.NODE][actionsById[id].nodeId]
              : actionsById[id].node || { x: 0, y: 0 },
        } as Node<INodeProps>;
      }
      function convertEdge(id, sourceId, targetId, sourceHandle?, attribs?) {
        return {
          id: id.toString(),
          source: `${sourceId}`,
          sourceHandle: sourceHandle ? `${sourceHandle}` : `${Handles.DEFAULT}`,
          target: `${targetId}`,
          targetHandle: `${Handles.DEFAULT}`,
          data: { id, sourceId, type: EntityTypes.MODULE_OUT, ...(attribs || {}) },
          type: 'smooth-step',
        } as Edge;
      }

      /**
   * `detached` is a walk from a node the course start does not reach. Those are
   * only drawn where an author actually put them: a child with no saved
   * position is left out rather than given one, which is what used to lay a
   * whole stale chain out in a row of its own under the flow.
   */
  function addChildren(node: Node<INodeProps>, depth, detached = false) {
        //if (courseNodesById[node.data.id].type !== CourseNodeType.AUTO)

        if (!node || nodes.find(n => n.id === node.id)) return;

        nodes.push(node);
        let hasPlaceholders = false;

        node.data.childrenLength =
          Object.values(actionsById[node.data.id as number].outs).length + actionsById[node.data.id as number].childId
            ? 1
            : 0;
        node.data.children = [];

        const childId: number =
          actionsById[node.data.id as number]?.outs[Handles.DEFAULT]?.targetId || (node.data.childId as number);

        if (
          childId &&
          childId > 0 &&
          actionsById[childId] &&
          actionsById[childId].status > -1 &&
          childId !== node.data.id &&
          !nodes.find(n => n.id === childId.toString())
        ) {
          const child = convertNode(childId, depth + 1);
          if (!child) return;
          child.data.parentActionId = node.data.parentActionId ? node.data.id : null;

          if (!child.data.nodeId)
            child.position = child.data.origin = {
              x: node.position.x + (hasPlaceholders ? widthWithNode : width),
              y: node.position.y,
            };
          modAutoPositions[child.id] = child.position;
          if (child.data.parentActionId && node.data.children) node.data.children.push(child);
          addChildren(child, depth + 1, detached);
        }

        if (childId && childId < 0 && !nodes.find(n => n.id === childId.toString())) {
          const pos = {
            x: node.position.x + (hasPlaceholders ? widthWithNode : width),
            y: node.position.y,
          };
          const loadingNode: Node<INodeProps> = {
            id: `${childId}`,
            data: { id: childId, type: ActionType.SIMPLE, children: [], origin: pos, childId: null, delay: 0 },
            type: EntityTypes.ACTION,
            position: pos,
          } as Node<INodeProps>;
          nodes.push(loadingNode);
          edges.push(convertEdge(`${node.data.id}-${childId}`, node.data.id, childId, Handles.DEFAULT));
        }

        parseActionPlaceholders(actionsById[node.data.id as number], node.position, index)
          .reverse()
          .map((ph, i) => {
            index--;
            hasPlaceholders = true;
            nodes.push(ph);
            node.data.children.push(ph);
            if (!edges.find(e => e.id === ph.id.toString()))
              edges.push(
                convertEdge(ph.id, node.data.id, ph.id, ph.data.sourceHandle, {
                  ...ph.data,
                  parentNodeId: node.data.id,
                  placeholder: true,
                }),
              );
          });

        Object.values(actionsById[node.data.id].outs)
          .filter(
            (out: IModuleOut) =>
              out.targetId &&
              out.sourceHandle !== Handles.DEFAULT &&
              actionsById[out.targetId] &&
              !nodes.find(n => n.id === out.targetId.toString()),
          )
          .forEach((out: IModuleOut, i) => {
            const child = convertNode(out.targetId, null);
            if (!child) return;

            //child.parentNode = node.id;
            child.data.parentEntityId = out.id;
            child.data.parentActionId = node.data.id;
            child.data.parentHandle = out.sourceHandle;
            if (!child.data.nodeId) {
              child.position = child.data.origin = {
                x: node.position.x + (out.sourceHandle !== Handles.FAIL && hasPlaceholders ? widthWithNode : width),
                y: out.sourceHandle === Handles.FAIL ? node.position.y - height : node.position.y + height * (i + 1),
              };
            }
            node.data.children.push(child);
            addChildren(child, depth + 1, detached);
          });

        Object.values(actionsById[node.data.id].outs)
          .filter(
            (out: IModuleOut) =>
              out.targetId &&
              out.sourceHandle !== Handles.DEFAULT &&
              out.targetId < 0 &&
              !nodes.find(n => n.id === out.targetId.toString()),
          )
          .forEach((out: IModuleOut, i) => {
            const pos = {
              x: node.position.x + (out.sourceHandle !== Handles.FAIL && hasPlaceholders ? widthWithNode : width),
              y: out.sourceHandle === Handles.FAIL ? node.position.y - height : node.position.y + height * (i + 1),
            };
            const loadingNode: Node<INodeProps> = {
              id: `${out.targetId}`,
              data: {
                id: out.targetId,
                type: ActionType.SIMPLE,
                children: [],
                origin: pos,
                childId: null,
                delay: 0,
                parentActionId: node.data.id,
                parentHandle: out.sourceHandle,
              },
              type: EntityTypes.ACTION,
              position: pos,
            } as Node<INodeProps>;
            nodes.push(loadingNode);
            edges.push(convertEdge(`${node.data.id}-${out.targetId}`, node.data.id, out.targetId, out.sourceHandle));
          });
      }

      const ophans = Object.values(actionsById).filter(
        a =>
          !Object.values(actionsById).find(
            (ab: IActionDAO) =>
              ab.childId === a.id || Object.values(ab.outs).find((o: IModuleOut) => o.targetId === a.id),
          ),
      );

      const firstNode =
        actionsById[entity[EntityTypes.MODULE][id]?.actionId] ||
        minBy(
          ophans.filter((a: IActionDAO) => a.node),
          (a: any) => a.node.x,
        ) ||
        minBy(ophans, 'id');

      if (firstNode) addChildren(convertNode(firstNode.id, 1), 1);

      getEntities(entity, EntityTypes.MODULE_OUT, 'moduleId', id)
        .filter(mo => !!mo)
        .filter(mo => entity[EntityTypes.ACTION][mo.sourceId] && entity[EntityTypes.ACTION][mo.targetId])
        .forEach((mo: IModuleOut) => {
          if (!edges.find(e => e.id === mo.id.toString()))
            edges.push({
              id: mo.id.toString(),
              source: `${mo.sourceId}`,
              sourceHandle: `${mo.sourceHandle}`,
              target: `${mo.targetId}`,
              targetHandle: `${mo.targetHandle ?? Handles.DEFAULT}`,
              data: { id: mo.id, type: EntityTypes.MODULE_OUT, sourceHandle: mo.sourceHandle },
              type: 'smooth-step',
            });
        });

      const lastNode = maxBy(nodes, n => n.position.x);

      Object.values(actionsById)
        .filter(cn => !nodes.find(n => n.data.id === cn.id) && cn.id > 0)
        .forEach((cn, i) => {
          const node = convertNode(cn.id, nodes.length + i);
          if (!node.data.nodeId)
            node.position = modAutoPositions[node.id] || {
              x: (lastNode?.position.x || 0) + widthWithNode,
              y: (lastNode?.position.y || 0) + height,
            };
          addChildren(node, nodes.length + i, true);
        });

      if (entity[EntityTypes.MODULE][id]?.type === ModuleType.SCENARIO) {
        layoutScenarioNodes(nodes, actionsById);

        // Every other placeholder hangs off a parent action, so there was no way
        // to author a slide ahead of the opening one — the flow could only ever
        // grow forwards. This one sits to the left of the first node and, when
        // taken, becomes the module's action with the old first node as its child.
        const first = firstNode && nodes.find(n => n.id === `${firstNode.id}`);

        if (first) {
          index--;
          const startId = `ph-start-${firstNode.id}`;
          const position = {
            x: first.position.x - (SCENARIO_NODE_WIDTH + SCENARIO_COLUMN_GAP),
            y: first.position.y + (scenarioNodeHeight(first, actionsById) - SCENARIO_PLACEHOLDER_HEIGHT) / 2,
          };

          nodes.push({
            id: startId,
            type: EntityTypes.ACTION,
            position,
            data: {
              id: index,
              type: ActionType.FUTURE,
              first: true,
              childId: firstNode.id,
              children: [],
              origin: position,
              delay: 0,
            },
          } as Node<INodeProps>);

          edges.push(
            convertEdge(startId, startId, first.id, Handles.DEFAULT, {
              placeholder: true,
              first: true,
            }),
          );
        }
      }

      return {
        nodes,
        edges,
      };
    },
  ),
)(state => noteCacheKey('selectModuleNodesAndEdges', state.state[EntityTypes.MODULE] || 'unknown')) as unknown as (
  state: RootState,
) => { nodes: Node<INodeProps>[]; edges: Edge[] };

const courseAutoPositions = {};

export const selectCourseNodesAndEdges = createCachedSelector(
  entity,
  courseId,
  selectAll,
  (entity: IEntityReducer, courseId: number, selectAll: boolean) =>
    parseCourseNodesAndEdges(entity, courseId, selectAll),
)((state, id) => id || state.state[EntityTypes.COURSE] || 'unknown');

export function parseCourseNodesAndEdges(entity: IEntityReducer, id: number, selectAll?: boolean) {
  const course: ICourse = entity[EntityTypes.COURSE][id];
  const width = 500;

  const normalizePosition = position => ({
    ...(position || {}),
    x: typeof position?.x === 'number' ? position.x : 0,
    y: typeof position?.y === 'number' ? position.y : 0,
  });

  if (!course) return { nodes: [], edges: [] };

  // The review link sits outside the flow. `get COU` leaves it out, but the review
  // page lists it into the same bucket, and unconnected nodes are drawn below.
  const courseNodesById: IKeyArray<ICourseNodeDAO> = reduceArrayById(
    getEntities(entity, EntityTypes.COURSE_NODE, 'courseId', id)
      .filter(n => !isReviewNode(n))
      .map(n => parseCourseNodeDAO(entity, n.id)),
  );

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  function convertNode(id) {
    if (!courseNodesById[id]) return null;

    if (courseNodesById[id].childId) convertEdge(id, id, courseNodesById[id].childId, id);

    const persistedNode = courseNodesById[id].nodeId ? entity[EntityTypes.NODE][courseNodesById[id].nodeId] : null;
    const position = persistedNode
      ? normalizePosition(persistedNode)
      : courseNodesById[id].node
      ? normalizePosition(courseNodesById[id].node)
      : { x: 0, y: 0 };

    return {
      id: `${id}`,
      data: {
        id,
        childId: courseNodesById[id].childId,
        nodeId: courseNodesById[id].nodeId,
        moduleId: courseNodesById[id].moduleId,
        origin: position,
        children: [],
        type: courseNodesById[id].type,
        delay: 0,
      },
      children: [],
      type: EntityTypes.COURSE_NODE,
      position,
    } as Node<INodeProps>;
  }
  function convertEdge(id, sourceId, targetId, parentNodeId) {
    if (!edges.find(e => e.id === `${id}.${sourceId}.${targetId}`))
      edges.push({
        id: `${id}.${sourceId}.${targetId}`,
        source: `${sourceId}`,
        sourceHandle: `${Handles.DEFAULT}`,
        target: `${targetId}`,
        targetHandle: `${Handles.DEFAULT}`,
        data: { id: id, type: EntityTypes.COURSE_OUT, parentNodeId },
        type: 'smooth-step',
      } as Edge);
  }

  /**
   * `detached` is a walk from a node the course start does not reach. Those are
   * only drawn where an author actually put them: a child with no saved
   * position is left out rather than given one, which is what used to lay a
   * whole stale chain out in a row of its own under the flow.
   */
  function addChildren(node: Node<INodeProps>, depth, detached = false) {
    //if (courseNodesById[node.data.id].type !== CourseNodeType.AUTO)

    if (!node || nodes.find(n => n.id === node.id)) return;

    nodes.push(node);

    courseNodesById[node.data.id]?.courseOuts
      .reduce((prev, next) => [...prev, next.targetId], node.data.childId ? [node.data.childId] : [])
      .filter(id => !!courseNodesById[id] && courseNodesById[id].status > -1)
      .forEach((id, i) => {
        const child = convertNode(id);

        if (!child) return;
        if (detached && !selectAll && !child.data.nodeId) return;

        if (!child.data.nodeId || !node.position) {
          child.position = child.data.origin = {
            x: (node.position?.x || 0) + width,
            y: (node.position?.y || 0) + height * i * 2,
          };
          courseAutoPositions[child.id] = child.position;
        }

        addChildren(child, depth + 1, detached);
      });
  }

  if (course.courseNodeId) addChildren(convertNode(course.courseNodeId), 0);

  // Everything the walk from the start picked up — what a learner can get to.
  const reachableIds = new Set(nodes.map(n => n.id));

  getEntities(entity, EntityTypes.COURSE_OUT, 'courseId', id)
    .filter(mo => !!mo && mo.status > -1)
    .filter(mo => entity[EntityTypes.COURSE_NODE][mo.sourceId] && entity[EntityTypes.COURSE_NODE][mo.targetId])
    .forEach((mo: ICourseOut) => {
      // A trunk link is stored twice — as the node's `childId` and as a DEFAULT
      // course out — and both were being drawn, one exactly on top of the
      // other. Invisible while an edge was only a line; not once it carries a
      // delay label and a "+". The `childId` edge wins because deleting it
      // clears the child, which the out row alone would leave dangling; the
      // real row's delay comes across so nothing is lost with it.
      const drawn = edges.find(
        e => e.source === `${mo.sourceId}` && e.target === `${mo.targetId}` && e.sourceHandle === `${mo.sourceHandle}`,
      );

      if (drawn) {
        drawn.data = { ...drawn.data, delay: mo.delay };
        return;
      }

      edges.push({
        id: mo.id.toString(),
        source: `${mo.sourceId}`,
        sourceHandle: `${mo.sourceHandle}`,
        target: `${mo.targetId}`,
        targetHandle: `${mo.targetHandle}`,
        data: { id: mo.id, type: EntityTypes.COURSE_OUT, delay: mo.delay },
        type: 'smooth-step',
      });
    });

  const lastNode = maxBy(nodes, n => n.position?.x) || nodes[nodes.length - 1];

  Object.values(courseNodesById)
    .filter(
      cn =>
        cn.status > -1 &&
        !nodes.find(n => n.data.id === cn.id) &&
        // Not `courseAutoPositions`: that remembers where a node was placed
        // while it was still in the flow, and it outlives the node's removal.
        (selectAll || cn.nodeId),
    )
    .forEach((cn, i) => {
      const node = convertNode(cn.id);
      if (!node.data.nodeId || !node.position) {
        node.position = node.data.origin = courseAutoPositions[cn.id] || {
          x: (lastNode?.position.x || 0) + widthWithNode * 2,
          y: (lastNode?.position.y || 0) + height,
        };
      }

      addChildren(node, nodes.length + i, true);
    });

  if (selectAll) return { nodes, edges };

  const keep = inCourseNodeIds(
    nodes.map(n => n.id),
    reachableIds,
    edges,
  );

  return {
    nodes: nodes.filter(n => keep.has(n.id)),
    edges: edges.filter(e => keep.has(e.source) && keep.has(e.target)),
  };
}

/**
 * The nodes that are part of the course's flow, out of everything it has.
 *
 * A course keeps every step it has ever had. Tidy up parks the ones nothing
 * reaches in a row under the flow, and drawn they read as a second course: a
 * repeat of half the modules, some with a lone line up into the real one.
 *
 * Kept: everything the start reaches, and any section cut off from it — a
 * deleted step between two others leaves the rest of the flow unreachable, and
 * hiding that would hide the break an author needs to see to fix. Dropped:
 * steps with no connections at all, and lone lead-ins — a step nothing points
 * at, whose every way out goes to a step that is reached some other way. A
 * section's own first step is kept, because it is the only way into the next.
 */
export function inCourseNodeIds(
  nodeIds: string[],
  reachableIds: Set<string>,
  edges: Pick<Edge, 'source' | 'target'>[],
): Set<string> {
  const ids = new Set(nodeIds);
  const predecessors: Record<string, Set<string>> = {};
  const successors: Record<string, Set<string>> = {};

  edges.forEach(({ source, target }) => {
    if (source === target || !ids.has(source) || !ids.has(target)) return;
    (predecessors[target] ||= new Set()).add(source);
    (successors[source] ||= new Set()).add(target);
  });

  const keep = new Set(nodeIds.filter(id => reachableIds.has(id)));
  // Cut off from the start, but something still leads into it.
  const entered = nodeIds.filter(id => !keep.has(id) && predecessors[id]?.size);

  entered.forEach(id => keep.add(id));

  nodeIds
    .filter(id => !keep.has(id) && !predecessors[id]?.size)
    .forEach(id => {
      const leadsIn = [...(successors[id] || [])].some(
        target => entered.includes(target) && predecessors[target].size === 1,
      );

      if (leadsIn) keep.add(id);
    });

  return keep;
}
